import React, { useState, useRef, useEffect, useCallback } from 'react';
import ConversationSearch from './ConversationSearch';
import ConversationItem from './ConversationItem';
import api from '../../services/api';
import './ConversationList.css';

function ConversationList({
  conversations,
  selectedConversation,
  onSelectConversation,
  onDoubleClickConversation,
  onSearch,
  loading,
  onConversationCreated,
  onArchive,           // single archive (hover-3s)
  onClickArchived,     // clique em conversa arquivada (busca)
  onArchiveBatch,      // (conversations: array) => void  — fecha várias de vez
  searchResults = null,   // { contacts, messages } da busca global
  onSelectResult,         // fn(result) ao clicar num resultado global
  searching = false,      // true enquanto busca global carrega
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const menuRef = useRef(null);

  // ─── Estado do modo seleção múltipla ───
  const [selectionMode, setSelectionMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    onSearch(term);
  };

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // ─── Handlers do modo seleção ───

  const enterSelectionMode = useCallback(() => {
    setSelectionMode(true);
    setCheckedIds(new Set());
    setShowMenu(false);
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setCheckedIds(new Set());
  }, []);

  // ESC sai do modo seleção
  useEffect(() => {
    if (!selectionMode) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') exitSelectionMode();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [selectionMode, exitSelectionMode]);

  const toggleCheck = useCallback((conversation) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(conversation.id)) {
        next.delete(conversation.id);
      } else {
        next.add(conversation.id);
      }
      return next;
    });
  }, []);

  const handleConfirmBatch = useCallback(() => {
    if (checkedIds.size === 0) return;
    if (!onArchiveBatch || typeof onArchiveBatch !== 'function') return;

    const toArchive = conversations.filter((c) => checkedIds.has(c.id));
    onArchiveBatch(toArchive);

    exitSelectionMode();
  }, [checkedIds, conversations, onArchiveBatch, exitSelectionMode]);

  const checkedCount = checkedIds.size;

  const headerTitle = selectionMode
    ? (checkedCount === 1 ? '1 selecionada' : `${checkedCount} selecionadas`)
    : 'Conversas';

  // ─── Helpers de busca ───

  const highlightMatch = (text, term) => {
    if (!text || !term) return text;
    const idx = text.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ backgroundColor: '#fef08a', color: 'inherit', borderRadius: '2px', padding: '0 1px' }}>
          {text.slice(idx, idx + term.length)}
        </mark>
        {text.slice(idx + term.length)}
      </>
    );
  };

  const formatResultTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays === 0) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="conversation-list">
      <div className={`conversation-list-header ${selectionMode ? 'selection-mode' : ''}`}>
        {/* Em modo seleção: botão check à esquerda fecha o modo */}
        {selectionMode && (
          <button
            type="button"
            className="exit-selection-btn"
            onClick={exitSelectionMode}
            title="Sair da seleção"
            aria-label="Sair da seleção"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9L7 13L15 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        <h2>{headerTitle}</h2>

        {/* Menu 3 pontinhos só fora do modo seleção */}
        {!selectionMode && (
          <div className="header-right">
            <div className="menu-wrapper" ref={menuRef}>
              <button
                className="btn-menu-dots"
                onClick={() => setShowMenu(!showMenu)}
                title="Menu"
              >
                ⋮
              </button>
              {showMenu && (
                <div className="header-menu-dropdown">
                  <button onClick={enterSelectionMode}>
                    ✓ Selecionar conversas
                  </button>
                  <button onClick={() => { setShowNewChat(true); setShowMenu(false); }}>
                    💬 Nova conversa
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showNewChat && (
        <NewChatForm
          onCreated={(conversation) => {
            setShowNewChat(false);
            onConversationCreated?.(conversation);
          }}
          onCancel={() => setShowNewChat(false)}
        />
      )}

      <ConversationSearch
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />

      <div className="conversations">
        {/* Modo busca: resultados globais substituem a lista */}
        {searchResults !== null ? (
          <>
            {searching && (
              <div className="search-status">Buscando...</div>
            )}

            {!searching && searchResults.contacts?.length === 0 && searchResults.messages?.length === 0 && (
              <div className="empty-conversations">
                <p>Nenhum resultado para "{searchTerm}"</p>
              </div>
            )}

            {!searching && searchResults.contacts?.length > 0 && (
              <>
                <div className="search-section-label">CONTATOS</div>
                {searchResults.contacts.map((c) => (
                  <button
                    key={c.id}
                    className="search-result-row"
                    onClick={() => onSelectResult({ type: 'contact', ...c })}
                  >
                    <div className="search-result-avatar">
                      {c.avatar_url
                        ? <img src={c.avatar_url} alt="" className="search-result-avatar-img" />
                        : <div className="search-result-avatar-placeholder">{(c.display_name || '?')[0].toUpperCase()}</div>
                      }
                    </div>
                    <div className="search-result-info">
                      <span className="search-result-name">{highlightMatch(c.display_name, searchTerm)}</span>
                      <span className="search-result-sub">{c.phone}</span>
                    </div>
                  </button>
                ))}
              </>
            )}

            {!searching && searchResults.messages?.length > 0 && (
              <>
                <div className="search-section-label">MENSAGENS</div>
                {searchResults.messages.map((m) => (
                  <button
                    key={m.message_id}
                    className="search-result-row"
                    onClick={() => onSelectResult({ type: 'message', ...m })}
                  >
                    <div className="search-result-avatar">
                      {m.avatar_url
                        ? <img src={m.avatar_url} alt="" className="search-result-avatar-img" />
                        : <div className="search-result-avatar-placeholder">{(m.contact_name || '?')[0].toUpperCase()}</div>
                      }
                    </div>
                    <div className="search-result-info">
                      <div className="search-result-header">
                        <span className="search-result-name">{m.contact_name}</span>
                        <span className="search-result-time">{formatResultTime(m.created_at)}</span>
                      </div>
                      <span className="search-result-snippet">
                        {m.direction === 'outbound' && <span className="search-result-you">Você: </span>}
                        {highlightMatch(m.snippet, searchTerm)}
                      </span>
                    </div>
                  </button>
                ))}
              </>
            )}
          </>
        ) : (
          /* Modo normal: lista de conversas */
          <>
            {loading && conversations.length === 0 ? (
              <div className="loading">Carregando conversas...</div>
            ) : conversations.length === 0 ? (
              <div className="empty-conversations">
                <p>Nenhuma conversa encontrada</p>
              </div>
            ) : (
              conversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={conversation.id === selectedConversation?.id}
                  onClick={onSelectConversation}
                  onDoubleClick={onDoubleClickConversation}
                  onArchive={onArchive}
                  onClickArchived={onClickArchived}
                  selectionMode={selectionMode}
                  isChecked={checkedIds.has(conversation.id)}
                  onToggleCheck={toggleCheck}
                />
              ))
            )}
          </>
        )}
      </div>

      {/* Rodapé fixo no modo seleção: botão Fechar */}
      {selectionMode && (
        <div className="selection-footer">
          <button
            type="button"
            className="batch-archive-btn"
            onClick={handleConfirmBatch}
            disabled={checkedCount === 0}
          >
            Fechar
            {checkedCount > 0 && <span className="batch-count">{checkedCount}</span>}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── New Chat Form (preservado, sem mudanças) ─── */
function NewChatForm({ onCreated, onCancel }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const phoneRef = useRef(null);

  useEffect(() => { phoneRef.current?.focus(); }, []);

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7,11)}`;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
    setError('');
  };

  const handleSubmit = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Telefone inválido');
      return;
    }
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const fullPhone = digits.startsWith('55') ? `+${digits}` : `+55${digits}`;
      const result = await api.createContactAndConversation(fullPhone, name.trim());
      onCreated(result);
    } catch (err) {
      setError(err.message?.includes('409') ? 'Contato já existe' : 'Erro ao criar conversa');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="new-chat-form">
      <div className="new-chat-header">
        <span>Nova conversa</span>
        <button className="new-chat-close" onClick={onCancel}>✕</button>
      </div>
      <input
        ref={phoneRef}
        type="tel"
        placeholder="(77) 99999-9999"
        value={phone}
        onChange={handlePhoneChange}
        onKeyDown={handleKeyDown}
        className="new-chat-input"
      />
      <input
        type="text"
        placeholder="Nome do contato"
        value={name}
        onChange={(e) => { setName(e.target.value); setError(''); }}
        onKeyDown={handleKeyDown}
        className="new-chat-input"
      />
      {error && <span className="new-chat-error">{error}</span>}
      <button
        className="new-chat-btn"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? 'Criando...' : 'Iniciar conversa'}
      </button>
    </div>
  );
}

export default ConversationList;
